'use client';

import { useTranslation } from '@/features/i18n';
import { AddToWatchListButton } from '@/features/watchlist';
import { CategoryButton } from '@/ui/buttons/CategoryButton';
import { FadeOutOverlay } from '@/ui/FadeOutOverlay';
import { Artwork } from '@/ui/images/Artwork';
import { ArtworkLogo } from '@/ui/images/ArtworkLogo';
import { AgeRating } from '@/ui/metadata/AgeRating';
import {
  CategoryType,
  CuratedCategoryType,
} from '@vimond/services/content-discovery';
import { cn } from '@vimond/theme/tailwind';
import { useRouter } from 'next/navigation';

type Props = {
  category: CuratedCategoryType | CategoryType;
};

export const CategoryCard = ({
  category: categoryProp,
  className,
  ...props
}: Props & React.ComponentPropsWithoutRef<'article'>) => {
  const { t } = useTranslation();
  const router = useRouter();

  const category =
    (categoryProp as CuratedCategoryType).content !== undefined
      ? ((categoryProp as CuratedCategoryType).content as CategoryType)
      : (categoryProp as CategoryType);
  if (!category) return <></>;

  return (
    <div
      onClick={() => router.push(`/category/${category.id}`)}
      className="cursor-pointer"
    >
      <article
        {...props}
        className={cn(
          'border-background-secondary group relative mx-1 box-content h-full border transition-all ease-in',
          className && className,
        )}
      >
        <div className="relative flex h-[230px] cursor-pointer flex-col">
          {category.images?.defaultUrl && (
            <Artwork
              alt=""
              src={category.images?.defaultUrl}
              location="portal-carousel"
              fallbackToDefault
              fill
              sizes="25vw"
              className="object-cover"
            />
          )}
          <FadeOutOverlay />
        </div>

        <div className="absolute left-0 right-0 top-0 z-[100] hidden h-[230px] text-white lg:block">
          <div className="border-background-secondary bg-background-secondary absolute bottom-0 left-0 right-0 top-0 border opacity-0 transition-all duration-300 ease-out group-hover:scale-110 group-hover:opacity-100 group-hover:delay-700">
            <div className="absolute hidden h-full w-full flex-col group-hover:flex">
              {category.images?.defaultUrl && (
                <Artwork
                  alt=""
                  src={category.images.defaultUrl}
                  location="portal-carousel"
                  fallbackToDefault
                  fill
                  sizes="25vw"
                  className="object-cover"
                />
              )}
              <FadeOutOverlay />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-2">
              {category.images?.defaultUrl && (
                <div className="max-w-[30%]">
                  <ArtworkLogo src={category.images?.defaultUrl} />
                </div>
              )}
              <div className="flex items-center gap-1 ">
                <CategoryButton categoryId={category.id} />
                <AddToWatchListButton category={category} />
              </div>

              <div className="mx-1 mt-1 flex items-center gap-4 text-xs opacity-70">
                {category['age-rating'] && (
                  <AgeRating ageRating={category['age-rating']} />
                )}
                {category.season && (
                  <span>{t('season', { count: category.season })}</span>
                )}
                {category.categoryType === 'SHOW' &&
                  category.children.length > 0 && (
                    <span>
                      {t('seasons', { count: category.children.length })}
                    </span>
                  )}
              </div>

              <div className="mx-1 mt-1 w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs">
                {category.labels?.split(',').map((label) => (
                  <span key={label} className="mr-1 uppercase opacity-50">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>
      <div className="text-shadow mx-1 mt-2 text-left text-primary lg:mt-4">
        <h1 className="lg:text-md text-sm font-bold xl:text-lg">
          {category.title}
        </h1>
      </div>
    </div>
  );
};
